import { action, computed, observable } from 'mobx';
import { CXContainerableBlock } from '~/components/ClientExperience/OneFlow/models/cx-containerable-block.model';
import type { CXScheduleSessionBlock } from '~/components/ClientExperience/OneFlow/models/cx-schedule-session-block.model';
import type { CXRootStore } from '~/components/ClientExperience/stores/cx-root.store';
import type { FlowRevision } from '~/models/flow-revision.model';
import { Variable } from '~/models/variable.model';
import type { TScheduleTriggerType } from '~/types/builder.types';
import {
    IScheduleContainerBlockResponse,
    IScheduleContainerVariableValue,
    ISessionConfig,
    ScheduleContainerVariableClient
} from '~/types/server.types';
import { isFutureDate } from '~/utils/date.utils';
import {
    shortTimezoneToFullTimezone,
    getUserTimezoneWithFallback
} from '~/utils/timezone.utils';

export class CXScheduleContainerBlock extends CXContainerableBlock {
    type: 'schedule_container_block';
    @observable schedule_session_trigger: TScheduleTriggerType;
    @observable is_required: boolean;
    @observable session_config: ISessionConfig;
    @observable tempVariableValue: IScheduleContainerVariableValue;
    @observable session_config_hash: string;

    constructor(
        block: IScheduleContainerBlockResponse,
        flowRevision: FlowRevision<CXRootStore>,
        rootStore: CXRootStore
    ) {
        super(block, flowRevision, rootStore);
        this.type = block.type;
    }

    @computed
    get isPendingClientCompletion() {
        return this.is_required && !this.isSlotSelected;
    }

    get displayName() {
        return 'scheduler';
    }

    @computed
    get scheduleSessionBlock() {
        return this.childBlocks.find(
            block => block.type === 'schedule_session_block'
        ) as CXScheduleSessionBlock;
    }

    get connectedVariable(): Variable<IScheduleContainerVariableValue> {
        return super.connectedVariable;
    }

    @computed
    get variableValue() {
        return this.tempVariableValue || this.connectedVariable?.value;
    }

    @computed
    get sessionConfig() {
        const sessionSelectionBlock = this.flow.sessionSelectionBlock;

        if (sessionSelectionBlock) {
            return sessionSelectionBlock.firstSelectedService?.session_config;
        }

        return this.session_config;
    }

    @computed
    private get status() {
        return this.connectedVariable?.value.status;
    }

    @computed
    get isSlotSelected() {
        return !!this.tempVariableValue;
    }

    @computed
    get isScheduled() {
        return this.status === 'scheduled';
    }

    @computed
    get isDefault() {
        return this.status === 'default' && !this.tempVariableValue;
    }

    @computed
    get isCanceled() {
        return this.status === 'canceled';
    }

    @computed
    get googleCalendarLink() {
        return this.connectedVariable.value?.google_calendar_link;
    }

    @computed
    get dateTimeStart() {
        return this.variableValue?.date_time_start;
    }

    @computed
    get sessionName() {
        return this.variableValue?.session_name;
    }

    @computed
    get sessionDuration() {
        return this.variableValue?.session_duration;
    }

    @computed
    get allowCanceling() {
        return this.connectedVariable.value?.allow_canceling;
    }

    @computed
    get allowRescheduling() {
        return this.connectedVariable.value?.allow_rescheduling;
    }

    @computed
    get wasSessionConfigChanged() {
        return this.connectedVariable.value?.was_session_config_changed;
    }

    @computed
    get ownerId() {
        return this.connectedVariable.value?.owner_id;
    }

    @computed
    get selectedDate() {
        return this.variableValue?.date_time_start;
    }

    @computed
    get scheduledDate() {
        return this.isScheduled ? this.selectedDate : undefined;
    }

    @computed
    get isPastMeeting() {
        return this.isScheduled && !this.isFutureMeeting;
    }

    @computed
    get isFutureMeeting() {
        return isFutureDate(this.selectedDate);
    }

    @computed
    get clientTimezone() {
        return this.variableValue?.client_timezone;
    }

    @computed
    get displayTimezone() {
        const { isPreview } = this.rootStore.clientPortalConfigStore;

        const timezone = isPreview ? this.sessionTimezone : this.clientTimezone;

        return timezone || getUserTimezoneWithFallback();
    }

    @computed
    get sessionTimezone() {
        return shortTimezoneToFullTimezone[
            this.sessionConfig?.session_timezone
        ];
    }

    @computed
    get sessionClient(): ScheduleContainerVariableClient {
        return this.variableValue?.client;
    }

    @computed
    get isBeforeChargePage() {
        const chargeBlock = this.flowRevision.chargeBlock;
        if (!chargeBlock) {
            return false;
        }

        const scheduleBlockPage = this.page;
        const chargePage = chargeBlock.page;
        return scheduleBlockPage.order < chargePage.order;
    }

    @computed
    get isRequireToPay() {
        return (
            this.flowRevision.hasInvoiceBlock &&
            !this.flow.isSomePaymentPaid &&
            this.isBeforeChargePage &&
            this.schedule_session_trigger === 'once_invoice_paid'
        );
    }

    @computed
    get isValidForSubmission() {
        return !this.is_required || this.isSlotSelected || this.isScheduled;
    }

    @action
    setTempVariableValue(value: IScheduleContainerVariableValue) {
        this.tempVariableValue = value;
    }
}
